// Dice rolling utility functions

/**
 * Parse a dice notation string (e.g., "2d6+3") and return structured data
 * @param {string} notation - Dice notation like "2d6+3" or "1d20"
 * @returns {object} Parsed dice data
 */
export function parseDice(notation) {
  if (!notation) return { count: 0, sides: 0, modifier: 0 };
  
  const match = notation.match(/(\d+)?d(\d+)([+-]\d+)?/i);
  if (!match) {
    // Check if it's just a modifier
    const modOnly = parseInt(notation);
    if (!isNaN(modOnly)) {
      return { count: 0, sides: 0, modifier: modOnly };
    }
    return { count: 0, sides: 0, modifier: 0 };
  }
  
  return {
    count: parseInt(match[1] || 1),
    sides: parseInt(match[2]),
    modifier: parseInt(match[3] || 0)
  };
}

/**
 * Roll dice based on notation string
 * @param {string} notation - Dice notation like "2d6+3"
 * @returns {object} Roll result with total and individual rolls
 */
export function rollDice(notation) {
  const parsed = parseDice(notation);
  const rolls = [];
  let total = parsed.modifier;
  
  // If it's just a modifier, return it
  if (parsed.count === 0 && parsed.sides === 0) {
    return {
      notation,
      total: parsed.modifier,
      rolls: [],
      modifier: parsed.modifier,
      breakdown: `${parsed.modifier}`
    };
  }
  
  // Roll each die
  for (let i = 0; i < parsed.count; i++) {
    const roll = Math.floor(Math.random() * parsed.sides) + 1;
    rolls.push(roll);
    total += roll;
  }
  
  // Build breakdown string
  let breakdown = '';
  if (rolls.length > 0) {
    breakdown = `${parsed.count}d${parsed.sides}(${rolls.join('+')})`;
    if (parsed.modifier !== 0) {
      breakdown += ` ${parsed.modifier >= 0 ? '+' : ''}${parsed.modifier}`;
    }
  }
  
  return {
    notation,
    total,
    rolls,
    modifier: parsed.modifier,
    breakdown,
    dice: parsed
  };
}

/**
 * Roll multiple dice groups and combine results
 * @param {string} notation - Complex notation like "2d6+1d4+3"
 * @returns {object} Combined roll results
 */
export function rollComplexDice(notation) {
  if (!notation) return { total: 0, groups: [], breakdown: '' };
  
  // Split by + or - while keeping the operators
  const parts = notation.split(/(?=[+-])/);
  const groups = [];
  let total = 0;
  
  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    
    // Check if it's a dice notation or just a number
    if (trimmed.includes('d')) {
      const result = rollDice(trimmed);
      groups.push(result);
      total += result.total;
    } else {
      const modifier = parseInt(trimmed);
      if (!isNaN(modifier)) {
        total += modifier;
        groups.push({
          notation: trimmed,
          total: modifier,
          rolls: [],
          modifier,
          breakdown: `${modifier}`
        });
      }
    }
  }
  
  const breakdown = groups.map(g => g.breakdown).join(' + ');
  
  return {
    notation,
    total,
    groups,
    breakdown
  };
}

/**
 * Roll with advantage or disadvantage
 * @param {string} notation - Base dice notation (usually "1d20")
 * @param {string} mode - "advantage", "disadvantage", or "normal"
 * @returns {object} Roll result
 */
export function rollWithAdvantage(notation, mode = 'normal') {
  if (mode === 'normal') {
    return rollDice(notation);
  }
  
  const roll1 = rollDice(notation);
  const roll2 = rollDice(notation);
  
  if (mode === 'advantage') {
    const result = roll1.total >= roll2.total ? roll1 : roll2;
    return {
      ...result,
      advantage: true,
      otherRoll: roll1.total >= roll2.total ? roll2 : roll1,
      breakdown: `Advantage: ${result.breakdown} (other: ${roll1.total >= roll2.total ? roll2.total : roll1.total})`
    };
  } else if (mode === 'disadvantage') {
    const result = roll1.total <= roll2.total ? roll1 : roll2;
    return {
      ...result,
      disadvantage: true,
      otherRoll: roll1.total <= roll2.total ? roll2 : roll1,
      breakdown: `Disadvantage: ${result.breakdown} (other: ${roll1.total <= roll2.total ? roll2.total : roll1.total})`
    };
  }
  
  return roll1;
}

/**
 * Check if a d20 roll is a critical (nat 20) or critical failure (nat 1)
 * @param {number} roll - The d20 roll result
 * @returns {object} Critical status
 */
export function checkCritical(roll) {
  return {
    isCritical: roll === 20,
    isCriticalFailure: roll === 1,
    roll
  };
}

/**
 * Format a roll result for display
 * @param {object} result - Roll result from rollDice
 * @returns {string} Formatted string
 */
export function formatRollResult(result) {
  if (!result) return '';

  let output = `Total: ${result.total}`;
  if (result.breakdown) {
    output += ` [${result.breakdown}]`;
  }

  if (result.advantage) {
    output += ' (Advantage)';
  } else if (result.disadvantage) {
    output += ' (Disadvantage)';
  }

  return output;
}

/**
 * Apply exhaustion penalties to d20 rolls based on D&D 5e/5.5e rules
 * [PURPOSE]: Implement exhaustion effects on d20 rolls for ability checks, attacks, and saves
 * [WHY]: D&D 5e rules impose disadvantage at certain exhaustion levels; 5.5e uses -N penalties
 * [INTEGRATION]: Called by all d20 rolling functions (ability checks, attacks, saves)
 *
 * @param {number} exhaustionLevel - Current exhaustion level (0-6 for 5e, 0-10 for 5.5e)
 * @param {string} exhaustionRules - Rules version: '5e' or '5.5e'
 * @param {string} rollType - Type of roll: 'ability_check', 'attack', 'saving_throw'
 * @param {string} currentMode - Current advantage mode: 'normal', 'advantage', 'disadvantage'
 * @returns {object} - { mode: string, penalty: number, description: string }
 */
export function applyExhaustionToD20(exhaustionLevel, exhaustionRules = '5e', rollType = 'ability_check', currentMode = 'normal') {
  // No exhaustion = no penalty
  if (!exhaustionLevel || exhaustionLevel === 0) {
    return { mode: currentMode, penalty: 0, description: '' };
  }

  // [D&D 5e 2014 RULES]: Specific disadvantage rules per exhaustion level
  if (exhaustionRules === '5e') {
    // Level 1: Disadvantage on ability checks
    if (exhaustionLevel >= 1 && rollType === 'ability_check') {
      // [D&D RULE]: Disadvantage cancels advantage
      const finalMode = currentMode === 'advantage' ? 'normal' : 'disadvantage';
      return {
        mode: finalMode,
        penalty: 0,
        description: 'Exhaustion 1+: Disadvantage on ability checks'
      };
    }

    // Level 3: Disadvantage on attack rolls and saving throws
    if (exhaustionLevel >= 3 && (rollType === 'attack' || rollType === 'saving_throw')) {
      const finalMode = currentMode === 'advantage' ? 'normal' : 'disadvantage';
      return {
        mode: finalMode,
        penalty: 0,
        description: 'Exhaustion 3+: Disadvantage on attacks & saves'
      };
    }

    return { mode: currentMode, penalty: 0, description: '' };
  }

  // [D&D 5.5e 2024 RULES]: -N penalty to all d20 rolls
  if (exhaustionRules === '5.5e') {
    // Levels 1-9: -N penalty (level 10 = death, handled elsewhere)
    const penalty = -Math.min(exhaustionLevel, 9);
    return {
      mode: currentMode,
      penalty,
      description: `Exhaustion ${exhaustionLevel}: ${penalty} to all d20 rolls`
    };
  }

  return { mode: currentMode, penalty: 0, description: '' };
}

export default {
  parseDice,
  rollDice,
  rollComplexDice,
  rollWithAdvantage,
  checkCritical,
  formatRollResult,
  applyExhaustionToD20
};